// src/features/unit/utils/conversionLogic.ts
import Big from 'big.js';
import { Unit } from '../types/unitConverterTypes';

// Configure big.js for high precision
Big.DP = 30; // Set decimal places to a high number for precision
Big.RM = Big.roundHalfUp; // Set rounding mode

/**
 * Converts a value from one unit to another using functional conversion definitions.
 * @param value The numeric value to convert (as a string to avoid precision loss).
 * @param fromUnit The unit object to convert from.
 * @param toUnit The unit object to convert to.
 * @returns The converted value as a string.
 */
export const convertUnits = (value: string, fromUnit: Unit, toUnit: Unit): string => {
    // Return empty string if input is invalid or empty
    if (!value || isNaN(Number(value)) || !fromUnit || !toUnit) {
        return '';
    }

    try {
        const valueBig = new Big(value);

        // 1. Convert the input value to the base unit value using the 'toBase' function
        const baseValue = fromUnit.toBase(valueBig);

        // 2. Convert the base unit value to the target unit value using the 'fromBase' function
        const result = toUnit.fromBase(baseValue);

        // Format the result: remove trailing zeros after the decimal point
        // but keep the integer part if it ends in zero (e.g., 10, 100).
        let resultString = result.toFixed(); // Use toFixed to avoid scientific notation
        if (resultString.includes('.')) {
            resultString = resultString.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '');
        }

        return resultString;

    } catch (error) {
        console.error("Unit conversion calculation error:", error);
        return ''; // Return empty on calculation error
    }
};